<template>
    <!-- Status indicator with tooltip for last bounce info -->
    <div
        :class="['flex items-center space-x-2', !showLabel && 'cursor-help']"
        v-tooltip.top="tooltipMessages.length > 0 ? tooltipMessages.join('\n') : null"
    >
        <div v-if="email.engagementScore && email.engagementScore > 2" class="h-2 w-2 rounded-full bg-green-400"></div>
        <div
            v-else-if="email.engagementScore && email.engagementScore <= 2"
            class="h-2 w-2 rounded-full bg-yellow-400"
        ></div>
        <div v-else class="h-2 w-2 rounded-full bg-red-400"></div>
        <span v-if="showLabel" class="text-md text-gray-900"
            >{{ tooltipMessages[0]
            }}<span v-if="tooltipMessages.length > 1" class="text-gray-500">
                +{{ tooltipMessages.length - 1 }}</span
            ></span
        >
    </div>
</template>

<script lang="ts" setup>
// Email status indicator component
// Shows engagement score and last bounce tooltip for an email
import { computed } from "vue"
import { EmailAddress } from "@/shared/models/email"
import { formatters } from "@/shared/utils/dateUtils"

// Define props for the email object
const props = defineProps<{ email: EmailAddress; showLabel?: boolean; enableTooltip?: boolean }>()

/**
 * Computes the tooltip text for the last bounce date.
 * Returns a formatted string or fallback if not available.
 */
const tooltipMessages = computed<string[]>(() => {
    const messages: string[] = []
    try {
        if (props.email.lastHardBounce) {
            messages.push(
                `Last hard bounce: ${formatters.formatHumanReadableDate(new Date(props.email.lastHardBounce), true)}`
            )
        }
        if (props.email.lastSoftBounce) {
            messages.push(
                `Last soft bounce: ${formatters.formatHumanReadableDate(new Date(props.email.lastSoftBounce), true)}`
            )
        }
        if (props.email.optOut) {
            messages.push(`Opt out: ${formatters.formatHumanReadableDate(new Date(props.email.optOut), true)}`)
        }
        if (props.email.spamComplaint) {
            messages.push(
                `Spam complaint: ${formatters.formatHumanReadableDate(new Date(props.email.spamComplaint), true)}`
            )
        }
        if (messages.length > 0) {
            return messages
        }
        if (
            !props.email.lastHardBounce &&
            !props.email.lastSoftBounce &&
            !props.email.optOut &&
            !props.email.spamComplaint &&
            props.email.engagementScore === 2
        ) {
            return ["Unverified"]
        }
        return ["No issue"]
    } catch (error) {
        return ["No issue"]
    }
})
</script>
